import { type ConstraintCatalogEntry, type ConstraintLevel, constraintCatalog } from '@bp/shared-planung-utils';
import { Button, Card, Checkbox, EmptyState, Input, Select, SelectOptionType } from '@bp/ui-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { usePermissionChecker } from '../../../../../../hooks/usePermissionChecker';
import {
  useSetTimetableVersionConstraintSettingsMutation,
  useTimetableVersionConstraintSettingsQuery,
} from '../../../../../../types/planung-graphql-client-defs';
import { showSuccessSaveToast, showUserErrorToast } from '../../../../../../utils/toast';

/*
 * Globaler Constraint-Katalog pro Stundenplan-Version ("Constraint-Gewichtung"). Rendert den geteilten
 * Solver-Katalog (@bp/shared-planung-utils) gruppiert nach Kategorie; pro Constraint Toggle (enabled),
 * Level (nur wenn levelEditable) und Gewicht (nur wenn weightSource OVERRIDE). Speichert NUR Abweichungen
 * vom Default als JSON-Blob auf TimetableVersion.constraintSettings (leer ⇒ Solver-Defaults, score-exakt).
 * Rohes urql, da das GraphQL-Feld noch nicht im Codegen ist (wie die übrigen Bedingungs-Tabs).
 */

type Setting = { enabled: boolean; level: ConstraintLevel; weight: number };
type StoredSetting = { id: string; enabled?: boolean; level?: ConstraintLevel; weight?: number };

const LEVELS: ConstraintLevel[] = ['HARD', 'MEDIUM', 'SOFT'];

const defaultFor = (entry: ConstraintCatalogEntry): Setting => ({
  enabled: true,
  level: entry.defaultLevel,
  weight: entry.defaultWeight,
});

const isDefault = (entry: ConstraintCatalogEntry, s: Setting): boolean =>
  s.enabled && s.level === entry.defaultLevel && s.weight === entry.defaultWeight;

// Tier-Familien/Dependencies (family != null) werden als EINE Zeile mit EINEM Gewicht dargestellt: die Stufe
// (hard/medium/soft) wird pro Bedingung/Karte über die Priorität gewählt, nicht hier — das eine Gewicht gilt
// für die wirksame Stufe. Singletons (family == null) bleiben einzeln.
const TIER_LABEL = /\s*\((hart|mittel|weich)\)\s*$/i;
const familyBaseLabel = (rep: ConstraintCatalogEntry): string => rep.label.replace(TIER_LABEL, '').trim();
const representative = (entries: ConstraintCatalogEntry[]): ConstraintCatalogEntry =>
  entries.find((e) => e.id.endsWith('-medium')) ?? entries[0];
const splitFamilies = (entries: ConstraintCatalogEntry[]) => {
  const singles: ConstraintCatalogEntry[] = [];
  const families = new Map<string, ConstraintCatalogEntry[]>();
  for (const entry of entries) {
    if (entry.family == null) {
      singles.push(entry);
      continue;
    }
    const list = families.get(entry.family) ?? [];
    list.push(entry);
    families.set(entry.family, list);
  }
  return { singles, families: [...families.values()] };
};

export const PlanTimetableConstraintSettings = () => {
  const { t } = useTranslation();
  const { versionUuid } = useParams();
  // Einzelgewichte (numerische Constraint-Gewichte) sind ein technisches Power-User-Feature und nur für
  // omni-admins sichtbar; reguläre Nutzer sehen weiterhin Toggle (enabled) und Level.
  const permissions = usePermissionChecker();
  const isOmniAdmin = permissions?.isOmniAdmin() ?? false;
  // Stundenplan View sieht die Einstellungen, ändert sie aber nicht.
  const canEdit = permissions?.canWriteTimetable() ?? false;

  const [{ data, fetching, error }, refetch] = useTimetableVersionConstraintSettingsQuery({
    variables: { versionUuid: versionUuid ?? '' },
    pause: !versionUuid,
    requestPolicy: 'cache-and-network',
  });
  const [, updateSettings] = useSetTimetableVersionConstraintSettingsMutation();

  const stored: Record<string, StoredSetting> = useMemo(() => {
    const raw = data?.timetableVersionConstraintSettings;
    if (!raw) return {};
    try {
      const arr = JSON.parse(raw) as StoredSetting[];
      return Object.fromEntries(arr.map((s) => [s.id, s]));
    } catch {
      return {};
    }
  }, [data]);

  const initial: Record<string, Setting> = useMemo(() => {
    const result: Record<string, Setting> = {};
    for (const entry of constraintCatalog.constraints) {
      const base = defaultFor(entry);
      const s = stored[entry.id];
      result[entry.id] = s
        ? { enabled: s.enabled ?? base.enabled, level: s.level ?? base.level, weight: s.weight ?? base.weight }
        : base;
    }
    return result;
  }, [stored]);

  const [settings, setSettings] = useState<Record<string, Setting>>(initial);
  // Seed from the loaded blob: on version change always; otherwise only while the user has NOT edited yet —
  // so a background cache-and-network response never discards in-flight edits (P0: silent data loss).
  const dirtyRef = useRef(false);
  const seededVersionRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (seededVersionRef.current !== versionUuid || !dirtyRef.current) {
      setSettings(initial);
      seededVersionRef.current = versionUuid;
      dirtyRef.current = false;
    }
  }, [initial, versionUuid]);

  const byCategory = useMemo(() => {
    const groups: Record<string, ConstraintCatalogEntry[]> = {};
    for (const entry of constraintCatalog.constraints) {
      (groups[entry.category] ??= []).push(entry);
    }
    return groups;
  }, []);

  const levelOptions: SelectOptionType[] = useMemo(
    () => LEVELS.map((l) => ({ label: t(`constraintCatalog.level.${l}`), value: l })),
    [t],
  );

  const update = (id: string, patch: Partial<Setting>) => {
    dirtyRef.current = true;
    setSettings((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  // Familien-Patch: dasselbe enabled/Gewicht auf ALLE Tier-Varianten der Familie anwenden.
  const updateMany = (ids: string[], patch: Partial<Setting>) => {
    dirtyRef.current = true;
    setSettings((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = { ...next[id], ...patch };
      return next;
    });
  };

  const handleSave = async () => {
    if (!versionUuid) return;
    // Nur editierbare Felder serialisieren — sonst würde level/weight für gesperrte bzw. FACT/COMPUTED-
    // Constraints auf den Katalog-Default gepinnt (Backend-Wire akzeptiert partielle Einträge {id, enabled, …}).
    const blob: StoredSetting[] = constraintCatalog.constraints
      .filter((entry) => !isDefault(entry, settings[entry.id]))
      .map((entry) => {
        const s = settings[entry.id];
        const out: StoredSetting = { id: entry.id, enabled: s.enabled };
        if (entry.levelEditable) out.level = s.level;
        if (entry.weightSource === 'OVERRIDE') out.weight = s.weight;
        return out;
      });
    const { error: saveError } = await updateSettings({ versionUuid, data: JSON.stringify(blob) });
    if (saveError) {
      showUserErrorToast({ error: saveError });
      return;
    }
    dirtyRef.current = false;
    showSuccessSaveToast();
    refetch({ requestPolicy: 'network-only' });
  };

  const handleReset = () => {
    dirtyRef.current = true;
    const reset: Record<string, Setting> = {};
    for (const entry of constraintCatalog.constraints) reset[entry.id] = defaultFor(entry);
    setSettings(reset);
  };

  if (!versionUuid) {
    return <EmptyState title={t('constraintCatalog.noVersion')} padding='xl' />;
  }

  // Query-Fehler sichtbar machen — sonst stünde data=undefined und die Seite zeigte alle Defaults,
  // was echte gespeicherte Overrides verdecken (und beim Speichern überschreiben) würde.
  if (error) {
    return <EmptyState title={t('constraintCatalog.loadError')} padding='xl' />;
  }

  return (
    <div className='constraint-catalog'>
      {canEdit && (
        <div className='tks__row px-6 py-4' style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button hierarchy='tertiary' onClick={handleReset}>
            {t('constraintCatalog.reset')}
          </Button>
          <Button hierarchy='primary' onClick={handleSave} disabled={fetching}>
            {t('common.save')}
          </Button>
        </div>
      )}

      {Object.entries(byCategory).map(([category, entries]) => (
        <Card
          key={category}
          className='mb-4'
          header={{ headline: t(`constraintCatalog.category.${category}`, { defaultValue: category }) }}
        >
          {(() => {
            const { singles, families } = splitFamilies(entries);
            return (
              <>
                {singles.map((entry) => {
                  const s = settings[entry.id] ?? defaultFor(entry);
                  return (
                    <div
                      key={entry.id}
                      className='tks__row'
                      style={{ alignItems: 'center', gap: '1rem', padding: '0.25rem 0' }}
                    >
                      <div className='tks__col col-xs-6'>
                        <Checkbox
                          name={`${entry.id}.enabled`}
                          label={entry.label}
                          checked={s.enabled}
                          disabled={!canEdit}
                          onChange={(e) => update(entry.id, { enabled: e.target.checked })}
                        />
                      </div>
                      <div className='tks__col col-xs-3'>
                        {entry.levelEditable ? (
                          <Select
                            name={`${entry.id}.level`}
                            options={levelOptions}
                            value={levelOptions.find((o) => o.value === s.level) ?? null}
                            disabled={!canEdit}
                            onChange={(opt) => {
                              const value = (opt as SelectOptionType | null)?.value as ConstraintLevel | undefined;
                              if (value) update(entry.id, { level: value });
                            }}
                            label={t('constraintCatalog.level.label')}
                            menuPosition='fixed'
                          />
                        ) : (
                          <span className='tks__text--hint' title={t('constraintCatalog.levelLocked')}>
                            {t(`constraintCatalog.level.${entry.defaultLevel}`)} 🔒
                          </span>
                        )}
                      </div>
                      {isOmniAdmin && (
                        <div className='tks__col col-xs-3'>
                          <Input
                            type='number'
                            name={`${entry.id}.weight`}
                            label={t('constraintCatalog.weight')}
                            min={0}
                            value={String(s.weight)}
                            disabled={entry.weightSource !== 'OVERRIDE'}
                            tooltipText={
                              entry.weightSource !== 'OVERRIDE' ? t('constraintCatalog.weightFactHint') : undefined
                            }
                            onChange={(e) => {
                              const raw = e.target.value;
                              // leeres Feld zulassen (→ 0); negatives Gewicht auf 0 klemmen (kein Reward via Negativwert).
                              update(entry.id, { weight: raw === '' ? 0 : Math.max(0, Number(raw) || 0) });
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {families.map((fam) => {
                  // Eine Zeile je Familie: ein enabled + ein Gewicht für alle Tier-Varianten; Stufe je Bedingung/Karte.
                  const rep = representative(fam);
                  const s = settings[rep.id] ?? defaultFor(rep);
                  const ids = fam.map((e) => e.id);
                  const weightEditable = rep.weightSource === 'OVERRIDE';
                  return (
                    <div
                      key={rep.family ?? rep.id}
                      className='tks__row'
                      style={{ alignItems: 'center', gap: '1rem', padding: '0.25rem 0' }}
                    >
                      <div className='tks__col col-xs-6'>
                        <Checkbox
                          name={`${rep.family}.enabled`}
                          label={familyBaseLabel(rep)}
                          checked={s.enabled}
                          disabled={!canEdit}
                          onChange={(e) => updateMany(ids, { enabled: e.target.checked })}
                        />
                      </div>
                      <div className='tks__col col-xs-3'>
                        <span className='tks__text--hint' title={t('constraintCatalog.levelPerCondition')}>
                          {t('constraintCatalog.levelPerConditionShort')}
                        </span>
                      </div>
                      {isOmniAdmin && (
                        <div className='tks__col col-xs-3'>
                          <Input
                            type='number'
                            name={`${rep.family}.weight`}
                            label={t('constraintCatalog.weight')}
                            min={0}
                            value={String(s.weight)}
                            disabled={!weightEditable}
                            tooltipText={!weightEditable ? t('constraintCatalog.weightFactHint') : undefined}
                            onChange={(e) => {
                              const raw = e.target.value;
                              updateMany(ids, { weight: raw === '' ? 0 : Math.max(0, Number(raw) || 0) });
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            );
          })()}
        </Card>
      ))}
    </div>
  );
};
